import { useEffect, useMemo, useRef, useState, ViewTransition } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import type { LatestWorkflowForEntityResponse } from '@/shared/hooks/api/workflows'
import {
  useApplicabilityPacks,
  useGetProduct,
  type ProductLawKey,
} from '@/shared/hooks/api/product'
import { Button } from '@/components/ui/button'
import { cn } from '@/shared/lib/utils'
import { ProductLawApplicabilityBadge } from './ProductLawApplicabilityBadge'
import {
  buildProductDiscoverChamberState,
  type DiscoverChamberLaw,
  type DiscoverChamberQuestion,
  type DiscoverQuestionBeadState,
} from '../utils/productDiscoverChamber'
import { isProductDiscoverWorkflowRunning } from '../utils/productDiscoverWorkflow'
import {
  localizeLawQuestionnaireQuestionLabel,
  useLawQuestionnaireTranslation,
} from '../utils/localizeLawQuestionnaireQuestion'

type V2ProductDiscoverScopeChamberProps = {
  productId: string
  workflow: LatestWorkflowForEntityResponse | undefined
  open: boolean
  onOpenChange: (open: boolean) => void
}

const COMPLETE_HANDOFF_MS = 1800

function beadClassName(state: DiscoverQuestionBeadState): string {
  if (state === 'running') {
    return 'bg-sky-300 shadow-[0_0_12px_rgba(125,211,252,0.95)] motion-safe:animate-pulse'
  }
  if (state === 'yes') {
    return 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.55)]'
  }
  if (state === 'no') {
    return 'bg-slate-500'
  }
  if (state === 'gap') {
    return 'border border-amber-400 bg-transparent dark:border-amber-300'
  }
  return 'bg-slate-300/80 dark:bg-white/20'
}

type LawConstellationProps = {
  lawState: DiscoverChamberLaw
  selectedBeadId: string | null
  onSelectQuestion: (question: DiscoverChamberQuestion) => void
}

function beadId(law: ProductLawKey, questionKey: string): string {
  return `${law}:${questionKey}`
}

function questionResultLabel(
  state: DiscoverQuestionBeadState,
  translate: (key: string) => string
): string {
  if (state === 'yes') {
    return translate('v2.pages.products.detail.discover.chamber.questionYes')
  }
  if (state === 'no') {
    return translate('v2.pages.products.detail.discover.chamber.questionNo')
  }
  if (state === 'gap') {
    return translate('v2.pages.products.detail.discover.chamber.questionGap')
  }
  if (state === 'running') {
    return translate(
      'v2.pages.products.detail.discover.chamber.questionRunning'
    )
  }
  return ''
}

function questionResultClassName(state: DiscoverQuestionBeadState): string {
  if (state === 'yes') {
    return 'text-emerald-600 dark:text-emerald-300'
  }
  if (state === 'no') {
    return 'text-slate-500 dark:text-slate-400'
  }
  if (state === 'gap') {
    return 'text-amber-600 dark:text-amber-300'
  }
  if (state === 'running') {
    return 'text-sky-600 dark:text-sky-300/90'
  }
  return 'text-muted-foreground'
}

function captionQuestionForLaw(
  lawState: DiscoverChamberLaw,
  selectedBeadId: string | null,
  lastCompletedKey: string | null,
  runningCycleIndex: number
): DiscoverChamberQuestion | null {
  const selected = lawState.questions.find((question) => {
    return selectedBeadId === beadId(lawState.law, question.questionKey)
  })
  if (selected != null) {
    return selected
  }
  if (lastCompletedKey != null) {
    const completed = lawState.questions.find((question) => {
      return question.questionKey === lastCompletedKey
    })
    if (
      completed != null &&
      completed.state !== 'pending' &&
      completed.state !== 'running'
    ) {
      return completed
    }
  }
  const runningQuestions = lawState.questions.filter((question) => {
    return question.state === 'running'
  })
  if (runningQuestions.length === 0) {
    return null
  }
  return (
    runningQuestions[runningCycleIndex % runningQuestions.length] ??
    runningQuestions[0]
  )
}

function LawConstellation({
  lawState,
  selectedBeadId,
  onSelectQuestion,
}: LawConstellationProps) {
  const { t, i18n } = useLawQuestionnaireTranslation(lawState.law)
  const translateQuestionLabel = (question: DiscoverChamberQuestion) =>
    localizeLawQuestionnaireQuestionLabel(
      t,
      lawState.law,
      { questionKey: question.questionKey, label: question.label },
      i18n.language
    )

  const isRunning = lawState.phase === 'running'
  const runningCount = lawState.questions.filter((question) => {
    return question.state === 'running'
  }).length
  const [runningCycleIndex, setRunningCycleIndex] = useState(0)
  const [lastCompletedKey, setLastCompletedKey] = useState<string | null>(null)
  const previousStatesRef = useRef<Record<string, DiscoverQuestionBeadState>>(
    {}
  )

  useEffect(() => {
    let newestCompletedKey: string | null = null
    for (const question of lawState.questions) {
      const previousState = previousStatesRef.current[question.questionKey]
      if (
        previousState === 'running' &&
        (question.state === 'yes' ||
          question.state === 'no' ||
          question.state === 'gap')
      ) {
        newestCompletedKey = question.questionKey
      }
    }
    previousStatesRef.current = Object.fromEntries(
      lawState.questions.map((question) => [
        question.questionKey,
        question.state,
      ])
    )
    if (newestCompletedKey != null) {
      setLastCompletedKey(newestCompletedKey)
    }
  }, [lawState.questions])

  useEffect(() => {
    if (lastCompletedKey == null) {
      return
    }
    const timeoutId = window.setTimeout(() => {
      setLastCompletedKey(null)
    }, 3200)
    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [lastCompletedKey])

  useEffect(() => {
    if (selectedBeadId != null && selectedBeadId.startsWith(`${lawState.law}:`)) {
      return
    }
    if (lastCompletedKey != null) {
      return
    }
    if (runningCount <= 1) {
      return
    }
    const intervalId = window.setInterval(() => {
      setRunningCycleIndex((index) => index + 1)
    }, 2800)
    return () => {
      window.clearInterval(intervalId)
    }
  }, [lastCompletedKey, lawState.law, runningCount, selectedBeadId])

  const captionQuestion = captionQuestionForLaw(
    lawState,
    selectedBeadId,
    lastCompletedKey,
    runningCycleIndex
  )
  const resultLabel =
    captionQuestion != null
      ? questionResultLabel(captionQuestion.state, t)
      : ''
  const captionTitle =
    captionQuestion == null
      ? undefined
      : [
          translateQuestionLabel(captionQuestion),
          captionQuestion.rationale,
        ]
          .filter((part) => part != null && part.length > 0)
          .join(' — ')

  const answeredQuestionCount = lawState.questions.filter((question) => {
    return question.state !== 'pending' && question.state !== 'running'
  }).length
  const showCompactQuestionProgress = lawState.questions.length > 12

  return (
    <section
      className={cn(
        'w-full rounded-2xl border px-3 py-2.5 backdrop-blur-md',
        isRunning
          ? 'border-sky-400/60 bg-sky-50 shadow-[0_0_28px_rgba(56,189,248,0.18)] dark:border-sky-400/50 dark:bg-sky-400/10 dark:shadow-[0_0_36px_rgba(56,189,248,0.25)]'
          : 'border-border/80 bg-card/90 shadow-sm dark:border-white/10 dark:bg-white/5 dark:shadow-none'
      )}
    >
      <div className="mb-2 flex min-h-5 items-start justify-between gap-2">
        <h3 className="min-w-0 text-xs font-semibold leading-snug tracking-wide text-foreground">
          {t(`v2.applicability.laws.${lawState.law}`)}
        </h3>
        {showCompactQuestionProgress && (
          <span className="shrink-0 font-mono text-[10px] tabular-nums text-muted-foreground">
            {t('v2.pages.products.detail.discover.chamber.progress', {
              answered: String(answeredQuestionCount),
              total: String(lawState.questions.length),
            })}
          </span>
        )}
        {lawState.verdict != null && (
          <ProductLawApplicabilityBadge
            status={lawState.verdict}
            className="h-5 shrink-0 px-1.5 text-[10px] dark:border-white/20 dark:bg-white/10 dark:text-slate-100"
          />
        )}
        {lawState.skipped && lawState.verdict == null && (
          <span className="shrink-0 text-[10px] text-muted-foreground">
            {t('v2.pages.products.detail.discover.verdicts.skipped')}
          </span>
        )}
      </div>
      <ul
        className="flex max-h-20 min-h-5 flex-wrap gap-1.5 overflow-y-auto overflow-x-hidden overscroll-contain"
        aria-label={t(`v2.applicability.laws.${lawState.law}`)}
      >
        {lawState.questions.map((question) => (
          <li key={question.questionKey}>
            <button
              type="button"
              title={translateQuestionLabel(question)}
              className={cn(
                'block size-2.5 rounded-full transition-all duration-300',
                beadClassName(question.state),
                selectedBeadId ===
                  beadId(lawState.law, question.questionKey) &&
                  'ring-2 ring-sky-500 ring-offset-1 ring-offset-background dark:ring-white dark:ring-offset-[#070b16]'
              )}
              onClick={() => {
                onSelectQuestion(question)
              }}
            >
              <span className="sr-only">{translateQuestionLabel(question)}</span>
            </button>
          </li>
        ))}
      </ul>
      <div className="mt-2 border-t border-border/70 pt-2 dark:border-white/10">
        <ViewTransition name={`discover-chamber-${lawState.law}`}>
          {captionQuestion == null ? (
            <div key="empty" className="h-8" />
          ) : (
            <div
              key={captionQuestion.questionKey}
              className="grid h-8 grid-cols-[minmax(0,1fr)_auto] items-start gap-x-2"
            >
              <p
                title={captionTitle}
                className={cn(
                  'h-8 line-clamp-2 text-[11px] leading-4',
                  captionQuestion.state === 'running'
                    ? 'text-sky-700 dark:text-sky-100/90'
                    : 'text-muted-foreground dark:text-slate-300'
                )}
              >
                {translateQuestionLabel(captionQuestion)}
              </p>
              <span
                className={cn(
                  'h-4 min-w-[4.25rem] shrink-0 text-right text-[10px] font-medium leading-4 tracking-wide',
                  resultLabel.length > 0
                    ? questionResultClassName(captionQuestion.state)
                    : 'invisible'
                )}
              >
                {resultLabel.length > 0 ? resultLabel : '—'}
              </span>
            </div>
          )}
        </ViewTransition>
      </div>
    </section>
  )
}

function ProgressRing({
  answeredCount,
  totalCount,
}: {
  answeredCount: number
  totalCount: number
}) {
  const radius = 58
  const circumference = 2 * Math.PI * radius
  const progress = totalCount === 0 ? 0 : answeredCount / totalCount
  const offset = circumference * (1 - progress)

  return (
    <svg className="size-[9.5rem] -rotate-90" viewBox="0 0 140 140" aria-hidden>
      <circle
        cx="70"
        cy="70"
        r={radius}
        fill="none"
        className="stroke-slate-200 dark:stroke-[rgba(255,255,255,0.08)]"
        strokeWidth="6"
      />
      <circle
        cx="70"
        cy="70"
        r={radius}
        fill="none"
        stroke="url(#chamber-ring)"
        strokeWidth="6"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        className="transition-[stroke-dashoffset] duration-500 motion-reduce:transition-none"
      />
      <defs>
        <linearGradient id="chamber-ring" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor="#7dd3fc" />
          <stop offset="100%" stopColor="#34d399" />
        </linearGradient>
      </defs>
    </svg>
  )
}

export function V2ProductDiscoverScopeChamber({
  productId,
  workflow,
  open,
  onOpenChange,
}: V2ProductDiscoverScopeChamberProps) {
  const { t } = useTranslation('common')
  const { data: product } = useGetProduct(productId)
  const { data: packResponse } = useApplicabilityPacks()
  const [selectedBead, setSelectedBead] = useState<{
    law: ProductLawKey
    question: DiscoverChamberQuestion
  } | null>(null)
  const chamber = useMemo(
    () => buildProductDiscoverChamberState(workflow, packResponse?.packs),
    [packResponse?.packs, workflow]
  )
  const isRunning = isProductDiscoverWorkflowRunning(workflow)
  const productTitle =
    product?.name?.trim() || t('v2.pages.products.fallbackTitle')

  useEffect(() => {
    if (!open) {
      return
    }
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onOpenChange(false)
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [open, onOpenChange])

  useEffect(() => {
    if (!open || !chamber.isComplete || selectedBead != null) {
      return
    }
    const timeoutId = window.setTimeout(() => {
      onOpenChange(false)
    }, COMPLETE_HANDOFF_MS)
    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [chamber.isComplete, onOpenChange, open, selectedBead])

  if (!open) {
    if (!isRunning) {
      return null
    }

    return createPortal(
      <button
        type="button"
        className="fixed bottom-6 right-6 z-[80] rounded-full border border-sky-400/50 bg-background px-4 py-2 text-sm text-sky-700 shadow-lg shadow-sky-500/15 dark:border-sky-400/40 dark:bg-[#0b1220] dark:text-sky-100 dark:shadow-[0_0_24px_rgba(56,189,248,0.35)]"
        onClick={() => {
          onOpenChange(true)
        }}
      >
        {t('v2.pages.products.detail.discover.chamber.reopen')}
      </button>,
      document.body
    )
  }

  let chamberStatus: string | null = null
  if (chamber.followUpMessageKey != null) {
    chamberStatus = t(chamber.followUpMessageKey)
  } else if (chamber.isComplete) {
    chamberStatus = t('v2.pages.products.detail.discover.chamber.complete')
  } else if (chamber.isFailed) {
    chamberStatus =
      chamber.errorMessage ?? t('v2.pages.products.detail.discover.log.failed')
  } else if (isRunning && chamber.answeredCount === 0) {
    chamberStatus = t('v2.pages.products.detail.discover.chamber.scanning')
  }

  const highlightedBeadId =
    selectedBead != null
      ? beadId(selectedBead.law, selectedBead.question.questionKey)
      : null

  return createPortal(
    <div
      className="fixed inset-0 z-[80] overflow-hidden bg-slate-50 text-foreground dark:bg-[#070b16] dark:text-slate-100"
      role="dialog"
      aria-modal="true"
      aria-labelledby="discover-chamber-title"
      aria-busy={isRunning}
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(56,189,248,0.12),transparent_42%)] dark:bg-[radial-gradient(circle_at_center,rgba(56,189,248,0.16),transparent_42%)]" />
      <div className="pointer-events-none absolute inset-0 opacity-30 motion-safe:animate-[spin_80s_linear_infinite] motion-reduce:animate-none bg-[conic-gradient(from_90deg_at_50%_50%,transparent_0deg,rgba(14,165,233,0.06)_70deg,transparent_140deg)] dark:opacity-40 dark:bg-[conic-gradient(from_90deg_at_50%_50%,transparent_0deg,rgba(125,211,252,0.08)_70deg,transparent_140deg)]" />

      <div className="relative flex h-full flex-col">
        <header className="flex items-start justify-between gap-4 px-6 pt-6 md:px-8">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-sky-600 dark:text-sky-300/80">
              {t('v2.pages.products.detail.discover.chamber.kicker')}
            </p>
            <h2
              id="discover-chamber-title"
              className="mt-1 text-2xl font-semibold tracking-tight"
            >
              {t('v2.pages.products.detail.discover.chamber.title')}
            </h2>
            <p className="mt-3 max-w-lg text-xs leading-relaxed text-muted-foreground/70">
              {t('v2.pages.products.detail.discover.chamber.explainer')}
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            className="border-border bg-background/80 text-foreground hover:bg-muted dark:border-white/20 dark:bg-transparent dark:text-slate-100 dark:hover:bg-white/10"
            onClick={() => {
              onOpenChange(false)
            }}
          >
            {isRunning
              ? t('v2.pages.products.detail.discover.chamber.hide')
              : t('actions.close')}
          </Button>
        </header>

        <div className="relative min-h-0 flex-1 overflow-auto">
          <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-4 md:px-8">
            <div className="flex flex-col items-center text-center">
              <div className="relative flex size-36 items-center justify-center">
                <ProgressRing
                  answeredCount={chamber.answeredCount}
                  totalCount={chamber.totalCount}
                />
                <div className="absolute inset-0 flex flex-col items-center justify-center px-6">
                  <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                    {t('v2.pages.products.detail.discover.chamber.product')}
                  </p>
                  <p className="mt-1 line-clamp-2 text-sm font-semibold text-foreground dark:text-white">
                    {productTitle}
                  </p>
                  <p className="mt-2 font-mono text-lg tabular-nums text-sky-700 dark:text-sky-200">
                    {t('v2.pages.products.detail.discover.chamber.progress', {
                      answered: chamber.answeredCount,
                      total: chamber.totalCount,
                    })}
                  </p>
                </div>
              </div>
              {chamberStatus != null && (
                <p className="mt-2 max-w-xs text-xs text-muted-foreground">
                  {chamberStatus}
                </p>
              )}
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {chamber.laws.map((lawState) => (
                <LawConstellation
                  key={lawState.law}
                  lawState={lawState}
                  selectedBeadId={highlightedBeadId}
                  onSelectQuestion={(question) => {
                    setSelectedBead({ law: lawState.law, question })
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
